import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { HindiInput } from "./HindiInput";
import { WEEKDAYS_HI } from "../data/rssbData";
import { hinglishToHindi } from "../lib/translit";

const TYPES = [
  { v: "C", label: "स — सेंटर" },
  { v: "SC", label: "स.स. — सब-सेंटर" },
  { v: "SP", label: "स.प. — सब-पॉइंट" },
];
const DAYS = [0, 3, 4];
const EXTRA = [
  ["group", "समूह (Grp Id)"],
  ["mw", "MW"],
  ["pathiDuty", "पाठी ड्यूटी"],
  ["nSK", "SK संख्या"],
  ["nSR", "SR संख्या"],
  ["nBSK", "BSK संख्या"],
  ["nPathi", "पाठी संख्या"],
];

export const GharDialog = ({ open, onClose, ghar, onSave }) => {
  const [name, setName] = useState("");
  const [type, setType] = useState("C");
  const [days, setDays] = useState([0]);
  const [sec, setSec] = useState("");
  const [secMob, setSecMob] = useState("");
  const [ex, setEx] = useState({});

  useEffect(() => {
    if (open) {
      setName(ghar?.hi || "");
      setType(ghar?.type || "C");
      setDays(ghar?.days || [0]);
      setSec(ghar?.secretaryHi || "");
      setSecMob(ghar?.secretaryMobile || "");
      setEx({ ...Object.fromEntries(EXTRA.map(([k]) => [k, ghar?.[k] ?? (k === "group" ? 1 : k === "mw" ? "NO" : "")])), noLcd: !!ghar?.noLcd });
    }
  }, [open, ghar]);

  const toggle = (d) => setDays((s) => (s.includes(d) ? s.filter((x) => x !== d) : [...s, d].sort()));

  const save = async () => {
    const hi = (await hinglishToHindi(name.trim())).trim();
    if (!hi || !days.length) return;
    const secretaryHi = sec.trim() ? (await hinglishToHindi(sec.trim())).trim() : "";
    const extra = Object.fromEntries(EXTRA.map(([k]) => [k, k === "mw" || k === "pathiDuty" ? String(ex[k] ?? "").trim() : ex[k] === "" ? "" : Number(ex[k])]));
    onSave({ ...(ghar || { id: "g" + Date.now().toString(36) }), hi, en: /[A-Za-z]/.test(name) ? name.trim() : ghar?.en || hi, type, days, secretaryHi, secretaryMobile: secMob.trim(), ...extra, noLcd: !!ex.noLcd });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#fffdf7] border-[#6e1b1b]/40 text-[#3a1212] max-h-[90vh] overflow-y-auto" data-testid="ghar-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-dev">{ghar ? "सत्संग घर बदलें" : "नया सत्संग घर"}</DialogTitle>
          <DialogDescription className="sr-only">सत्संग घर विवरण</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-[#5a2a24]">नाम</Label>
            <HindiInput testid="ghar-name" value={name} onChange={setName} placeholder="e.g. Nighasan" className="mt-1 bg-[#f6eeda] border-[#e3d6b6] font-dev" />
          </div>
          <div>
            <Label className="text-xs text-[#5a2a24]">प्रकार</Label>
            <select data-testid="ghar-type" value={type} onChange={(e) => setType(e.target.value)} className="w-full mt-1 bg-[#f6eeda] border border-[#e3d6b6] rounded-md px-3 py-2 text-sm font-dev">
              {TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs text-[#5a2a24]">सत्संग के दिन</Label>
            <div className="flex gap-2 mt-1">
              {DAYS.map((d) => (
                <button
                  type="button"
                  key={d}
                  data-testid={`ghar-day-${d}`}
                  onClick={() => toggle(d)}
                  className={`px-3 py-1 rounded-md text-sm font-dev border ${days.includes(d) ? "bg-[#6e1b1b] text-[#f3ead6] border-[#431010]" : "bg-[#f6eeda] text-[#5a2a24] border-[#e3d6b6]"}`}
                >
                  {WEEKDAYS_HI[d]}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#5a2a24]">सेक्रेटरी का नाम</Label>
              <HindiInput testid="ghar-secretary" value={sec} onChange={setSec} className="mt-1 bg-[#f6eeda] border-[#e3d6b6] font-dev" />
            </div>
            <div>
              <Label className="text-xs text-[#5a2a24]">सेक्रेटरी मोबाइल</Label>
              <input data-testid="ghar-secretary-mobile" inputMode="tel" value={secMob} onChange={(e) => setSecMob(e.target.value)} className="w-full mt-1 bg-[#f6eeda] border border-[#e3d6b6] rounded-md px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {EXTRA.map(([k, label]) => (
              <div key={k}>
                <Label className="text-[11px] text-[#5a2a24]">{label}</Label>
                <input
                  data-testid={`ghar-${k}`}
                  value={ex[k] ?? ""}
                  inputMode={k === "mw" ? "text" : "numeric"}
                  onChange={(e) => setEx((s) => ({ ...s, [k]: e.target.value }))}
                  className="w-full mt-1 bg-[#f6eeda] border border-[#e3d6b6] rounded-md px-2 py-1.5 text-sm"
                />
              </div>
            ))}
          </div>
          <label className="flex items-center gap-2 text-sm font-dev text-[#3a1212]">
            <input type="checkbox" data-testid="ghar-nolcd" checked={!!ex.noLcd} onChange={(e) => setEx((s) => ({ ...s, noLcd: e.target.checked }))} className="accent-[#6e1b1b] w-4 h-4" />
            यहाँ सवाल-जवाब (LCD) सत्संग नहीं होता
          </label>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} className="text-[#5a2a24]">रद्द करें</Button>
          <Button data-testid="ghar-save" disabled={!name.trim() || !days.length} onClick={save} className="bg-[#6e1b1b] text-[#f3ead6] hover:bg-[#7a1f1f]">सहेजें</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
